// =========================
// DSCRGauge
// SVG semicircle gauge with red / yellow / green zones.
// Conventions used:
//   < 1.00  → red    (debt service exceeds NOI)
//   1.00–1.20 → yellow (marginal)
//   ≥ 1.20  → green  (healthy)
// =========================

import React from "react";

const RED = "#dc2626";
const YELLOW = "#f59e0b";
const GREEN = "#16a34a";
const TRACK = "#e5e7eb";
const TEXT = "#111827";
const SUBTLE = "#6b7280";

const MIN = 0;
const MAX = 2.5;

function zoneFor(dscr) {
  if (dscr < 1.0) return { label: "Below 1.0 — the loan payment costs more than the property earns", color: RED };
  if (dscr < 1.2) return { label: "Tight — barely covers the loan payment (1.0–1.2)", color: YELLOW };
  return { label: "Healthy — comfortably covers the loan (1.2 or higher)", color: GREEN };
}

function valueToAngle(v) {
  const clamped = Math.max(MIN, Math.min(MAX, v));
  const t = (clamped - MIN) / (MAX - MIN);
  return -180 + t * 180;
}

function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  const sweep = endDeg > startDeg ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
}

export default function DSCRGauge({ value }) {
  const dscr = typeof value === "number" && isFinite(value) ? value : 0;
  const z = zoneFor(dscr);

  // Extra top padding (cy lower in the viewBox, taller viewBox) keeps
  // the tick labels well clear of the top edge.
  const W = 280;
  const H = 200;
  const cx = W / 2;
  const cy = 158;
  const r = 110;
  const stroke = 18;

  const redEnd = valueToAngle(1.0);
  const yellowEnd = valueToAngle(1.2);
  const trackStart = -180;
  const trackEnd = 0;

  const needleAngle = valueToAngle(dscr);
  const needleEnd = polar(cx, cy, r - 4, needleAngle);

  const tickPos = (val) => {
    const a = (valueToAngle(val) * Math.PI) / 180;
    return { x: cx + Math.cos(a) * (r + 18), y: cy + Math.sin(a) * (r + 18) + 4 };
  };
  const t0 = tickPos(0);
  const t1 = tickPos(1.0);
  const t12 = tickPos(1.2);
  const tMax = tickPos(MAX);

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`DSCR ${dscr.toFixed(2)}`}>
        <path d={arcPath(cx, cy, r, trackStart, trackEnd)} stroke={TRACK} strokeWidth={stroke} fill="none" strokeLinecap="round" />
        <path d={arcPath(cx, cy, r, trackStart, redEnd)} stroke={RED} strokeWidth={stroke} fill="none" strokeLinecap="round" />
        <path d={arcPath(cx, cy, r, redEnd, yellowEnd)} stroke={YELLOW} strokeWidth={stroke} fill="none" />
        <path d={arcPath(cx, cy, r, yellowEnd, trackEnd)} stroke={GREEN} strokeWidth={stroke} fill="none" strokeLinecap="round" />

        <text x={t0.x} y={t0.y} fontSize="11" fill={SUBTLE} textAnchor="middle">0</text>
        <text x={t1.x} y={t1.y} fontSize="11" fill={SUBTLE} textAnchor="middle">1.0</text>
        <text x={t12.x} y={t12.y} fontSize="11" fill={SUBTLE} textAnchor="middle">1.2</text>
        <text x={tMax.x} y={tMax.y} fontSize="11" fill={SUBTLE} textAnchor="middle">2.5+</text>

        <line x1={cx} y1={cy} x2={needleEnd.x} y2={needleEnd.y} stroke={TEXT} strokeWidth="3" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="6" fill={TEXT} />
      </svg>

      <div style={{ marginTop: 8, fontSize: 36, fontWeight: 800, color: z.color, lineHeight: 1 }}>
        {dscr.toFixed(2)}
      </div>
      <div style={{ marginTop: 6, fontSize: 13, color: SUBTLE, textAlign: "center" }}>
        DSCR — <span style={{ color: z.color, fontWeight: 600 }}>{z.label}</span>
      </div>
    </div>
  );
}
